import React from 'react';

import BarChartBar, {
  BarChartBarProps,
  FADED_OPACITY,
} from 'ui-library/charting/internals/bar-chart/BarChartBar';
import ErrorBar from 'ui-library/charting/internals/ErrorBar';

import {BarChartAccessors, BarChartScales} from './BarChartTypes';

const MAX_ERROR_BAR_WIDTH = 20;

export type BarGroupBarProps = Omit<BarChartBarProps, 'height' | 'y'> & {
  errorValueAccessor?: BarChartAccessors['errorValueAccessor'];
  yAccessor: BarChartAccessors['yAccessor'];
  yScale: BarChartScales['yScale'];
};

export default function BarGroupBar({
  errorValueAccessor,
  yAccessor,
  yScale,
  ...barChartBarProps
}: BarGroupBarProps): JSX.Element | null {
  const {dataFrameRow, isFaded, width, x} = barChartBarProps;
  const yValue = yAccessor(dataFrameRow);

  const origin = yScale(0) as number;
  const y = yScale(yValue) as number;
  // Visx's BarGroup calculates the y position and height of bars incorrectly when there's negative
  // bars (it always uses the bottom of the chart as the origin), so we have to calculate these on our
  // own using the real origin.
  // https://github.com/airbnb/visx/blob/master/packages/visx-shape/src/shapes/BarGroup.tsx
  const barY = yValue >= 0 ? y : origin;
  const barHeight = yValue >= 0 ? origin - y : y - origin;

  if (barHeight < 0) {
    return null;
  }

  return (
    <>
      <BarChartBar {...barChartBarProps} y={barY} height={barHeight} />
      {errorValueAccessor && errorValueAccessor(dataFrameRow) != null && (
        <ErrorBar
          opacity={isFaded ? FADED_OPACITY : 1}
          x={x + width / 2}
          y1={yScale(yValue + errorValueAccessor(dataFrameRow)) as number}
          y2={yScale(yValue - errorValueAccessor(dataFrameRow)) as number}
          width={Math.min(width, MAX_ERROR_BAR_WIDTH)}
        />
      )}
    </>
  );
}
